/**
 * Engine public surface — `05-` §A.8.
 *
 * Phase A exports:
 *  - `buildSchedulingGraph(snapshot)` — memoized
 *  - `buildAnalysisGraph(snapshot)` — memoized
 *  - `topologicalSort(graph)` — memoized (wraps the util)
 *  - `findCycles(graph)` — memoized
 *  - `findCycleIfAdded(snapshot, candidate)` — candidate cycle check behind
 *    `gantt.isCircularLink` and the link-add/update guard
 *
 * Phase A stubs (throw until later phases):
 *  - `plan(snapshot, rootTaskId?, inclusive?)` — Phase C
 *  - `analyze(snapshot)` — Phase B
 *  - `getConstraintLimits(snapshot, taskId)` — Phase B
 *  - `resolveConstraintType(snapshot, taskId)` — Phase B
 *
 * Per D-02, `plan()` accepts `inclusive`. Per D-15, cycle members in
 * `analyze()` are omitted from slack maps (Phase B implements). Memoization
 * follows `05-` §G.2 — WeakMap-keyed by reference; same snapshot ⇒ same
 * result.
 */

import { memoize1 } from "./utils/memoize";
import {
  buildSchedulingGraph_unmemoized
} from "./graph/scheduling_graph";
import {
  buildAnalysisGraph_unmemoized
} from "./graph/analysis_graph";
import { expandStructuralLink, isSummaryDescendantPair } from "./graph/link_expansion";
import type { StructuralLinkEdge } from "./graph/link_expansion";
import {
  findCycles_unmemoized,
  connectedComponents_unmemoized,
  findCycleWithCandidate
} from "./utils/scc";
import type {
  Cycle,
  ConnectedGroup,
  Snapshot,
  LinkSnapshot,
  TaskID,
  NormalizedLinkID,
  PlanResult,
  AnalysisResult
} from "./types";
import type { SchedulingGraph, AnalysisGraph } from "./graph/types";
// Phase B: analyze() wired here. analyze_unmemoized lives in core/analysis/
// and consumes the memoized graph builders below.
import { analyze_unmemoized, getSlackBetween_unmemoized } from "./analysis/analyze";
// Phase C scaffolding: plan() body lives in core/planner/. Stub throws
// until Day 6 ships the fixed-point loop.
import { plan_unmemoized } from "./planner/plan";

// ─────────────────────────────────────────────────────────────────────────────
// Memoized graph builders
// ─────────────────────────────────────────────────────────────────────────────

export const buildSchedulingGraph: (snapshot: Snapshot) => SchedulingGraph =
  memoize1(buildSchedulingGraph_unmemoized);

export const buildAnalysisGraph: (snapshot: Snapshot) => AnalysisGraph =
  memoize1(buildAnalysisGraph_unmemoized);

// ─────────────────────────────────────────────────────────────────────────────
// Cycle detection
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Memoized cycle detection over a SchedulingGraph or AnalysisGraph.
 * The cache key is the graph reference; same graph ⇒ same cycles array.
 */
export const findCycles: (graph: SchedulingGraph | AnalysisGraph) => readonly Cycle[] =
  memoize1((graph: SchedulingGraph | AnalysisGraph): readonly Cycle[] => {
    const edges = graph.edges.map(e => ({ id: e.linkId, source: e.source, target: e.target }));
    return findCycles_unmemoized(graph.nodes.keys(), edges);
  });

/**
 * Memoized connected-components over the undirected projection of a
 * scheduling or analysis graph. Drives `gantt.getConnectedGroup`.
 */
export const connectedComponents: (graph: SchedulingGraph | AnalysisGraph) => readonly ConnectedGroup[] =
  memoize1((graph: SchedulingGraph | AnalysisGraph): readonly ConnectedGroup[] => {
    const edges = graph.edges.map(e => ({ id: e.linkId, source: e.source, target: e.target }));
    return connectedComponents_unmemoized(graph.nodes.keys(), edges);
  });

/**
 * `findCycleIfAdded(snapshot, candidate)` — "would adding this link close a
 * cycle?". The single candidate-cycle entry point: it backs both
 * `gantt.isCircularLink(link)` for a link that is not in the datastore yet
 * and the `onBeforeLinkAdd` / `onBeforeLinkUpdate` guard, exactly as v1
 * answered both from one `getLoopContainingLink`.
 *
 * Both the candidate and the stored links are expanded through
 * `analysisLeafReplicas`: a link to/from a derived summary stands for one link
 * per leaf descendant (v1 `_formatLink` → `_getImplicitLinks`). Without the
 * expansion a cycle that only closes through it (`A → Project` plus
 * `leaf-of-Project → A`) can still be drawn in the UI, and `findCycles()`
 * then reports the graph as cyclic — GS-3553.
 *
 * A summary ↔ own-descendant candidate is never circular (v1 `_formatLink`
 * bails out on that pair before building any relation), and neither are the
 * `s === t` replica pairs the expansion can produce.
 *
 * The check is deliberately STRUCTURAL — every stored link participates,
 * including links touching a completed task, which the analysis graph drops
 * under `use_progress`. v1 does the same for the link-add guard (v1's
 * `_preventCircularLink` switches `use_progress` off around the call) so that
 * turning progress-aware scheduling on cannot open a door for new cycles.
 * That is also why no `AnalysisGraph` is built here: `onBeforeLinkAdd` /
 * `onBeforeLinkUpdate` are latency-sensitive, and the expansion rule alone
 * (`graph/link_expansion.ts`) is all this needs.
 *
 * A candidate whose id belongs to a stored link REPLACES it (link update),
 * so the answer is about the graph as it would be, not as it is.
 *
 * Pure: does not mutate the snapshot. Caches per `05-` §G.2 — outer WeakMap
 * keyed by snapshot; inner Map keyed by
 * `${candidate.id}|${candidate.source}|${candidate.target}|${candidate.type}`
 * so the same logical candidate hits the cache regardless of object identity.
 */
const findCycleIfAddedCache = new WeakMap<Snapshot, Map<string, Cycle | null>>();

export function findCycleIfAdded(snapshot: Snapshot, candidate: LinkSnapshot): Cycle | null {
  let inner = findCycleIfAddedCache.get(snapshot);
  if (!inner) {
    inner = new Map();
    findCycleIfAddedCache.set(snapshot, inner);
  }
  const key = `${candidate.id}|${candidate.source}|${candidate.target}|${candidate.type}`;
  if (inner.has(key)) return inner.get(key) as Cycle | null;
  const result = findCycleIfAdded_impl(snapshot, candidate);
  inner.set(key, result);
  return result;
}

function findCycleIfAdded_impl(snapshot: Snapshot, candidate: LinkSnapshot): Cycle | null {
  if (isSummaryDescendantPair(snapshot, candidate.source, candidate.target)) return null;

  const candidateEdges = expandStructuralLink(snapshot, candidate);
  if (candidateEdges.length === 0) return null;

  // On `onBeforeLinkUpdate` the candidate carries the id of a link that IS
  // stored, with new endpoints — the graph to test is the stored one WITHOUT
  // that link. Filtering by id is a no-op for a plain add.
  const base = structuralEdges(snapshot).filter(e => e.id !== candidate.id);

  return findCycleWithCandidate(base, candidate.id, candidateEdges);
}

/**
 * Every stored link, expanded through derived summaries — the edge set the
 * candidate cycle check runs against. Memoized per snapshot: a link drag asks
 * the same question for many candidates within one revision.
 */
const structuralEdgesCache = new WeakMap<Snapshot, StructuralLinkEdge[]>();

function structuralEdges(snapshot: Snapshot): StructuralLinkEdge[] {
  const hit = structuralEdgesCache.get(snapshot);
  if (hit) return hit;
  const edges: StructuralLinkEdge[] = [];
  for (const link of snapshot.links) {
    for (const e of expandStructuralLink(snapshot, link)) edges.push(e);
  }
  structuralEdgesCache.set(snapshot, edges);
  return edges;
}

/**
 * The snapshot's own version of a stored link, or `undefined` when the id is
 * unknown to this snapshot. Lets `gantt.isCircularLink` tell "this is the
 * stored link" from "these are proposed endpoints for it" — the object an
 * `onBeforeLinkUpdate` handler receives is the LIVE datastore item, already
 * carrying the new endpoints, while the snapshot may still hold the old ones.
 * Memoized index: the dispatcher may ask once per link when painting.
 */
const linkIndexCache = new WeakMap<Snapshot, Map<NormalizedLinkID, LinkSnapshot>>();

export function getSnapshotLink(snapshot: Snapshot, linkId: NormalizedLinkID): LinkSnapshot | undefined {
  let index = linkIndexCache.get(snapshot);
  if (!index) {
    index = new Map();
    for (const link of snapshot.links) index.set(link.id, link);
    linkIndexCache.set(snapshot, index);
  }
  return index.get(linkId);
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase B / Phase C stubs
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Plan a schedule.
 *
 * @param snapshot   Immutable input (tasks, links, calendars, hierarchy, config).
 * @param rootTaskId If provided, plan only the connected component containing
 *                   this task. If undefined, plan all tasks.
 * @param inclusive  Per D-02: when `false`, restrict the planned set to
 *                   successors-of-root (excluding predecessors of root).
 *                   When `true` (default), include both predecessors and
 *                   successors in the connected component. Ignored when
 *                   `rootTaskId` is undefined.
 *
 * Pure. Same (snapshot, rootTaskId, inclusive) ⇒ same result.
 *
 * Phase A: not implemented. The signature is wired so `core/api.ts` exports
 * the full public surface; the body throws until Phase C delivers the
 * fixed-point planner per `04-` §16.
 */
// Memoization for plan(): keyed on (snapshot, rootTaskId|"", inclusive).
// The same logical call within one snapshot lifetime returns the same
// PlanResult — adapter consumers (e.g., `onAfterTaskUpdate` retriggering
// autoSchedule for the same dragged task) hit cache.
const planCache = new WeakMap<Snapshot, Map<string, PlanResult>>();

export function plan(snapshot: Snapshot, rootTaskId?: TaskID, inclusive: boolean = true): PlanResult {
  let inner = planCache.get(snapshot);
  if (!inner) {
    inner = new Map();
    planCache.set(snapshot, inner);
  }
  const key = `${rootTaskId === undefined ? "" : String(rootTaskId)}|${inclusive ? "1" : "0"}`;
  const hit = inner.get(key);
  if (hit) return hit;
  const result = plan_unmemoized(snapshot, rootTaskId, inclusive);
  inner.set(key, result);
  return result;
}

/**
 * Compute slack and critical-path data.
 *
 * Pure. Per D-13/D-16, depends only on `AnalysisConfig` (the locked-down
 * subset of SchedulingConfig). Cycle members are omitted from slack maps
 * per D-15.
 *
 * Phase B wires this to the analysis pipeline in `core/analysis/analyze.ts`.
 * The result is memoized per snapshot identity per `05-` §G.2 — same snapshot
 * reference ⇒ same AnalysisResult reference. Adapter consumers (e.g.,
 * `gantt.getTotalSlack(task)`) call this once per logical query batch.
 */
export const analyze: (snapshot: Snapshot) => AnalysisResult = memoize1(analyze_unmemoized);

/**
 * `getSlack(t1, t2)` — D-14 path-enumeration slack between two tasks in
 * minutes (engine standard unit per D-35). `Infinity` when no simple path
 * connects the pair in either direction; adapter consumer maps that to
 * v1's no-path date-arithmetic semantics at the public-method boundary.
 *
 * Not memoized at this level: the heavy work (graph build + slack pass)
 * is memoized inside analyze.ts's `_internals`. The DFS-with-min path
 * enumeration here is fast in typical inputs (MAX_DEPTH = 64).
 */
export function getSlackBetween(snapshot: Snapshot, t1: TaskID, t2: TaskID): number {
  return getSlackBetween_unmemoized(snapshot, t1, t2);
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase C Day 13 — public re-exports for ext/auto_scheduling.js consumers.
// `core/planner/constraints.ts` is the canonical home; this surface keeps the
// adapter import path consistent (one module, all engine entries).
// ─────────────────────────────────────────────────────────────────────────────

export { resolveConstraintType, getConstraintLimits } from "./planner/constraints";
export type { ConstraintLimits } from "./planner/constraints";
